import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import io from "socket.io-client";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  Alert,
  Card,
  Carousel,
  Col,
  DatePicker,
  Empty,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";

dayjs.locale("es");

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const groupBookingKey = (booking) =>
  booking.comboGroupId ? `combo-${booking.comboGroupId}` : `booking-${booking.id}`;

const currencyFormatter = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const DashboardPage = () => {
  const revenueCarouselRef = useRef(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(() => [
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/booking`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "No se pudo cargar el dashboard");
      }

      setBookings(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Dashboard fetchBookings:", error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleBookingsUpdate = () => {
      fetchBookings();
    };

    const handleAvailabilityUpdate = () => {
      fetchBookings();
    };

    socket.on("bookings:updated", handleBookingsUpdate);
    socket.on("availability:updated", handleAvailabilityUpdate);

    return () => {
      socket.off("bookings:updated", handleBookingsUpdate);
      socket.off("availability:updated", handleAvailabilityUpdate);
      socket.disconnect();
    };
  }, [fetchBookings]);

  const groupedBookings = useMemo(() => {
    const grouped = new Map();

    bookings.forEach((booking) => {
      const key = groupBookingKey(booking);
      const current = grouped.get(key);

      if (current) {
        current.items.push(booking);
        return;
      }

      grouped.set(key, {
        key,
        date: booking.date,
        time: booking.time,
        status: booking.status,
        user: booking.user,
        items: [booking],
      });
    });

    return Array.from(grouped.values()).map((group) => {
      const statuses = group.items.map((item) => item.status);
      const hasActive = statuses.some((status) =>
        ["reservado", "en_curso"].includes(status)
      );
      const hasCancelled = statuses.every((status) => status === "cancelado");
      const hasFinalized = statuses.every((status) => status === "finalizado");

      return {
        ...group,
        derivedStatus: hasCancelled
          ? "cancelado"
          : hasFinalized
          ? "finalizado"
          : hasActive
          ? "activo"
          : group.status,
        reservationValue: group.items.reduce(
          (sum, item) => sum + Number(item.service?.price || 0),
          0
        ),
        finalIncome: group.items.reduce(
          (sum, item) =>
            sum +
            Number(item.service?.priceTotal ?? item.service?.price ?? 0),
          0
        ),
      };
    });
  }, [bookings]);

  const filteredGroups = useMemo(() => {
    const [start, end] = range || [];
    if (!start || !end) {
      return groupedBookings;
    }

    return groupedBookings.filter((group) => {
      const current = dayjs(group.date);
      return (
        current.isSame(start, "day") ||
        current.isSame(end, "day") ||
        (current.isAfter(start, "day") && current.isBefore(end, "day"))
      );
    });
  }, [groupedBookings, range]);

  const summary = useMemo(() => {
    const activeGroups = filteredGroups.filter((group) =>
      ["activo", "reservado", "en_curso"].includes(group.derivedStatus)
    );
    const finalizedGroups = filteredGroups.filter(
      (group) => group.derivedStatus === "finalizado"
    );
    const cancelledGroups = filteredGroups.filter(
      (group) => group.derivedStatus === "cancelado"
    );
    const nonCancelledGroups = filteredGroups.filter(
      (group) => group.derivedStatus !== "cancelado"
    );

    return {
      totalTurns: filteredGroups.length,
      activeReservationValue: activeGroups.reduce(
        (sum, group) => sum + group.reservationValue,
        0
      ),
      finalizedIncome: finalizedGroups.reduce(
        (sum, group) => sum + group.finalIncome,
        0
      ),
      finalizedCount: finalizedGroups.length,
      cancelledCount: cancelledGroups.length,
      nonCancelledCount: nonCancelledGroups.length,
      finalizedPercentage: nonCancelledGroups.length
        ? Math.round((finalizedGroups.length / nonCancelledGroups.length) * 100)
        : 0,
    };
  }, [filteredGroups]);

  const employeeRanking = useMemo(() => {
    const byEmployee = new Map();

    filteredGroups.forEach((group) => {
      group.items.forEach((item) => {
        const employeeId = Number(item.employee?.id);
        if (!employeeId) {
          return;
        }

        const current = byEmployee.get(employeeId) || {
          employeeId,
          name: item.employee?.name || "Sin nombre",
          color: item.employee?.background_color || "#1677ff",
          turns: 0,
          amount: 0,
        };

        current.turns += 1;
        current.amount += Number(
          item.status === "finalizado"
            ? item.service?.priceTotal ?? item.service?.price ?? 0
            : item.service?.price ?? 0
        );

        byEmployee.set(employeeId, current);
      });
    });

    const maxTurns = Math.max(
      1,
      ...Array.from(byEmployee.values()).map((item) => item.turns)
    );

    return Array.from(byEmployee.values())
      .sort((a, b) => {
        if (b.turns === a.turns) {
          return b.amount - a.amount;
        }
        return b.turns - a.turns;
      })
      .map((item) => ({
        ...item,
        percent: Math.max(8, Math.round((item.turns / maxTurns) * 100)),
      }));
  }, [filteredGroups]);

  const dailyRevenue = useMemo(() => {
    const [start, end] = range || [];
    if (!start || !end) {
      return [];
    }

    const dayMap = new Map();
    let cursor = start.startOf("day");
    const lastDay = end.startOf("day");

    while (cursor.isBefore(lastDay, "day") || cursor.isSame(lastDay, "day")) {
      dayMap.set(cursor.format("YYYY-MM-DD"), {
        key: cursor.format("YYYY-MM-DD"),
        label: cursor.format("DD/MM"),
        fullLabel: cursor.format("D [de] MMMM"),
        amount: 0,
      });
      cursor = cursor.add(1, "day");
    }

    filteredGroups.forEach((group) => {
      const dayKey = dayjs(group.date).format("YYYY-MM-DD");
      const currentDay = dayMap.get(dayKey);
      if (!currentDay) {
        return;
      }

      const amount =
        group.derivedStatus === "finalizado"
          ? group.finalIncome
          : group.reservationValue;

      currentDay.amount += amount;
    });

    const values = Array.from(dayMap.values());
    const maxAmount = Math.max(1, ...values.map((item) => item.amount));

    return values.map((item) => ({
      ...item,
      height: item.amount
        ? Math.max(56, Math.round((item.amount / maxAmount) * 210))
        : 24,
    }));
  }, [filteredGroups, range]);

  const dailyRevenueSlides = useMemo(() => {
    const chunkSize = 8;
    const slides = [];

    for (let index = 0; index < dailyRevenue.length; index += chunkSize) {
      slides.push(dailyRevenue.slice(index, index + chunkSize));
    }

    return slides;
  }, [dailyRevenue]);

  const infoMessage =
    "El valor de reservas activas corresponde a turnos reservados o en curso. Al finalizar la atención, el precio total pasa a ingresos por servicios finalizados.";

  return (
    <div style={{ padding: 12 }}>
      <Card
        style={{
          borderRadius: 20,
          border: "1px solid #eef2f6",
          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.05)",
        }}
        bodyStyle={{ padding: 28 }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Text
              style={{
                color: "#3b82f6",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Resumen del negocio
            </Text>
            <Title level={2} style={{ margin: "8px 0 4px" }}>
              Dashboard
            </Title>
            <Text type="secondary">
              Rendimiento de reservas, equipo e ingresos.
            </Text>
          </Col>

          <Col xs={24} lg={10} style={{ textAlign: "right" }}>
            <RangePicker
              value={range}
              format="DD/MM/YYYY"
              onChange={(values) => {
                if (!values || values.length !== 2) {
                  return;
                }
                setRange(values);
              }}
              allowClear={false}
            />
          </Col>
        </Row>

        <Alert
          message={infoMessage}
          type="info"
          showIcon
          style={{
            marginTop: 20,
            marginBottom: 20,
            borderRadius: 12,
            border: "1px solid #b6dcff",
          }}
        />

        {loading ? (
          <div style={{ padding: "60px 0", textAlign: "center" }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} xl={6}>
                <Card bordered={false} style={metricCardStyle}>
                  <Statistic
                    title="Turnos"
                    value={summary.totalTurns}
                    prefix={<CalendarOutlined style={{ color: "#3b82f6" }} />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <Card bordered={false} style={metricCardStyle}>
                  <Statistic
                    title="Valor de reservas activas"
                    value={summary.activeReservationValue}
                    formatter={(value) =>
                      currencyFormatter.format(Number(value || 0))
                    }
                    prefix={
                      <DollarCircleOutlined style={{ color: "#10b981" }} />
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <Card bordered={false} style={metricCardStyle}>
                  <Statistic
                    title="Ingresos por servicios finalizados"
                    value={summary.finalizedIncome}
                    formatter={(value) =>
                      currencyFormatter.format(Number(value || 0))
                    }
                    prefix={
                      <DollarCircleOutlined style={{ color: "#8b5cf6" }} />
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <Card bordered={false} style={metricCardStyle}>
                  <Statistic
                    title="Finalizados"
                    value={summary.finalizedCount}
                    prefix={
                      <CheckCircleOutlined style={{ color: "#f59e0b" }} />
                    }
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col xs={24} xl={16}>
                <Card
                  title={
                    <Space>
                      <TeamOutlined />
                      Turnos por empleado
                    </Space>
                  }
                  style={panelCardStyle}
                >
                  {employeeRanking.length ? (
                    <Space
                      direction="vertical"
                      size={18}
                      style={{ width: "100%" }}
                    >
                      {employeeRanking.map((employee) => (
                        <div key={employee.employeeId}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              marginBottom: 6,
                              flexWrap: "wrap",
                            }}
                          >
                            <Text strong>{employee.name}</Text>
                            <Space split={<span style={{ color: "#d9d9d9" }}>|</span>}>
                              <Text type="secondary">
                                {employee.turns}{" "}
                                {employee.turns === 1 ? "turno" : "turnos"}
                              </Text>
                              <Text type="secondary">
                                {currencyFormatter.format(employee.amount)}
                              </Text>
                            </Space>
                          </div>
                          <div
                            style={{
                              height: 10,
                              width: "100%",
                              borderRadius: 999,
                              background: "#f3f4f6",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${employee.percent}%`,
                                height: "100%",
                                borderRadius: 999,
                                background: employee.color,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Empty description="No hay reservas en este período" />
                  )}
                </Card>
              </Col>

              <Col xs={24} xl={8}>
                <Card title="Estado del período" style={panelCardStyle}>
                  <div style={{ textAlign: "center", paddingTop: 8 }}>
                    <Progress
                      type="circle"
                      percent={summary.finalizedPercentage}
                      strokeColor="#10b981"
                      trailColor="#f0f2f5"
                      size={120}
                    />
                    <div style={{ marginTop: 16, color: "#6b7280" }}>
                      Turnos finalizados
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 24,
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: 16,
                    }}
                  >
                    <div style={statusRowStyle}>
                      <Text>No cancelados</Text>
                      <Text strong>{summary.nonCancelledCount}</Text>
                    </div>
                    <div style={statusRowStyle}>
                      <Text>Cancelados</Text>
                      <Text strong style={{ color: "#ef4444" }}>
                        {summary.cancelledCount}
                      </Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Card
              title="Valor de servicios por día"
              style={{ ...panelCardStyle, marginTop: 16 }}
              bodyStyle={{ padding: "20px 18px 18px" }}
              extra={
                dailyRevenueSlides.length > 1 ? (
                  <Space size={8}>
                    <button
                      type="button"
                      onClick={() => revenueCarouselRef.current?.prev()}
                      style={chartNavButtonStyle}
                    >
                      <LeftOutlined />
                    </button>
                    <button
                      type="button"
                      onClick={() => revenueCarouselRef.current?.next()}
                      style={chartNavButtonStyle}
                    >
                      <RightOutlined />
                    </button>
                  </Space>
                ) : null
              }
            >
              {dailyRevenue.length ? (
                <Carousel ref={revenueCarouselRef} dots adaptiveHeight>
                  {dailyRevenueSlides.map((slide, slideIndex) => (
                    <div key={`daily-revenue-slide-${slideIndex}`}>
                      <div style={dailyRevenueWrapperStyle}>
                        <div style={dailyRevenueGridStyle}>
                          {slide.map((day) => (
                            <div key={day.key} style={dailyRevenueItemStyle}>
                              <Text style={dailyRevenueValueStyle}>
                                {currencyFormatter.format(day.amount)}
                              </Text>
                              <div style={dailyRevenueBarRailStyle}>
                                <div
                                  title={`${day.fullLabel}: ${currencyFormatter.format(
                                    day.amount
                                  )}`}
                                  style={{
                                    ...dailyRevenueBarStyle,
                                    height: day.height,
                                    opacity: day.amount ? 1 : 0.35,
                                  }}
                                />
                              </div>
                              <Text
                                type="secondary"
                                style={dailyRevenueLabelStyle}
                              >
                                {day.label}
                              </Text>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </Carousel>
              ) : (
                <Empty description="No hay datos para graficar en este período" />
              )}
            </Card>
          </>
        )}
      </Card>
    </div>
  );
};

const metricCardStyle = {
  borderRadius: 18,
  border: "1px solid #f1f5f9",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
};

const panelCardStyle = {
  marginTop: 8,
  borderRadius: 18,
  border: "1px solid #f1f5f9",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
};

const statusRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 14,
};

const chartNavButtonStyle = {
  width: 34,
  height: 34,
  borderRadius: 10,
  border: "1px solid #dbe4f0",
  background: "#ffffff",
  color: "#1e293b",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
};

const dailyRevenueWrapperStyle = {
  overflowX: "auto",
  overflowY: "hidden",
  paddingBottom: 6,
};

const dailyRevenueGridStyle = {
  minWidth: 0,
  display: "grid",
  gridTemplateColumns: "repeat(8, minmax(72px, 1fr))",
  gap: 14,
  alignItems: "end",
};

const dailyRevenueItemStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 10,
};

const dailyRevenueValueStyle = {
  fontSize: 11,
  color: "#64748b",
  whiteSpace: "nowrap",
};

const dailyRevenueBarRailStyle = {
  height: 220,
  width: "100%",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
};

const dailyRevenueBarStyle = {
  width: "100%",
  maxWidth: 44,
  borderRadius: "12px 12px 8px 8px",
  background: "linear-gradient(180deg, #5fa8ff 0%, #2f80ed 100%)",
  boxShadow: "0 10px 22px rgba(47, 128, 237, 0.18)",
  transition: "height 0.25s ease",
};

const dailyRevenueLabelStyle = {
  fontSize: 11,
  whiteSpace: "nowrap",
};

export default DashboardPage;
